import { useContext, useEffect, useMemo, useRef } from 'react'

import type { BurnBatchRequest } from 'api/loyalty/requestLoyaltyPointsBurnCalculationBatch'
import submitLoyaltyPointsBurnCalculationRequest from 'actions/loyalty/submitLoyaltyPointsBurnCalculationRequest'
import { fetchAccumulatedOfferById } from 'actions/OfferActions'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import buildBurnKey from 'loyalty/lib/pointsCalculation/buildBurnKey'
import { take } from 'lib/array/arrayUtils'
import { getAllOffers } from 'selectors/offerSelectors'
import LoyaltySpendPointsPricingContext from 'loyalty/contexts/LoyaltySpendPointsPricingContext'
import type { SpendPointsSortOption } from './loyaltySpendPointsTypes'
import { getCashPriceCap } from './loyaltySpendPointsBurnUtils'
import { SPEND_POINTS_CAROUSEL_RENDER_LIMIT } from './loyaltySpendPointsConstants'

interface BurnRequests {
  standard: BurnBatchRequest
  /** Member price request — when present, its priceInPoints is preferred for sort/filter */
  member?: BurnBatchRequest
}

type GetBurnRequest = (offer: App.AnyOffer) => BurnRequests | undefined

interface Props {
  offerList: App.OfferList
  sortOption: SpendPointsSortOption
  getBurnRequest: GetBurnRequest
  /**
   * When burn candidates are empty (recommended sort, balance filter off), fall back to the
   * raw search order. Disable for points-first carousels that must never surface unpriced /
   * ineligible IDs (e.g. hotel flight bundles).
   * @default true
   */
  allowSearchOrderFallback?: boolean
}

interface Result {
  offerList: App.OfferList
  cashPriceCap?: number
}

function buildResolvedListCacheKey(
  listKey: string | undefined,
  sortOption: SpendPointsSortOption,
  showOffersWithinBalance: boolean,
  burnPointsLimit: number | undefined,
) {
  return `${listKey ?? ''}:${sortOption}:${showOffersWithinBalance ? burnPointsLimit ?? 'balance' : 'all'}`
}

/**
 * Points-based sort and filter are handled client-side because the offer list
 * search endpoints (unified, tours, cruises) have no concept of price-in-points.
 * Until search gains a `pointsPriceLte` / `sortBy=points` capability, burn
 * prices are resolved here via the burn-calc batch API and used to:
 *   1. derive a conservative cash-price ceiling passed as `priceLte` to search
 *      (product types convert at different rates, so the highest observed
 *      cash-per-point ratio is used to avoid over-filtering)
 *   2. filter and sort the returned offer IDs by their actual points cost
 */
function useLoyaltySpendPointsOfferListBurnSort({
  offerList,
  sortOption,
  getBurnRequest,
  allowSearchOrderFallback = true,
}: Props): Result {
  const dispatch = useAppDispatch()
  const offers = useAppSelector(getAllOffers)
  const offerErrors = useAppSelector(state => state.offer.offerErrors)
  const offersLoading = useAppSelector(state => state.offer.offersLoading)
  const cruiseOfferErrors = useAppSelector(state => state.cruise.cruiseOffersErrors)
  const customerId = useAppSelector(state => state.auth.account.memberId ?? '')
  const burnCalculations = useAppSelector(state => state.loyalty.pointsBurnBatchCalculations)
  const { burnPointsLimit, showOffersWithinBalance } = useContext(LoyaltySpendPointsPricingContext)
  const isPointsSort = sortOption === 'lowest-points' || sortOption === 'highest-points'
  const needsFullListProcessing = isPointsSort || showOffersWithinBalance
  const processingOfferIds = useMemo(
    () => needsFullListProcessing
      ? offerList.offerIds
      : take(offerList.offerIds, SPEND_POINTS_CAROUSEL_RENDER_LIMIT),
    [needsFullListProcessing, offerList.offerIds],
  )
  const resolvedListsRef = useRef<Map<string, App.OfferList>>(new Map())
  const resolvedListCacheKey = buildResolvedListCacheKey(
    offerList.key,
    sortOption,
    showOffersWithinBalance,
    burnPointsLimit,
  )

  useEffect(() => {
    // Search cruise ids use the `cruise:` prefix. fetchAccumulatedOfferById routes those
    // to the summary endpoint, which includes the departure pricing burn needs.
    for (const offerId of processingOfferIds) {
      dispatch(fetchAccumulatedOfferById(offerId))
    }
  }, [dispatch, processingOfferIds])

  const candidates = useMemo(() => processingOfferIds.flatMap(id => {
    const offer = offers[id]
    if (!offer) return []

    const requests = getBurnRequest(offer)
    return requests ? [{ id, requests }] : []
  }), [getBurnRequest, offers, processingOfferIds])

  const isWaitingForOfferDetails = processingOfferIds.some(id => {
    if (offerErrors[id] || cruiseOfferErrors[id]) {
      return false
    }

    if (offersLoading[id]) {
      return true
    }

    return !offers[id]
  })

  useEffect(() => {
    // Waiting for every candidate avoids multiple burn batches as individual
    // Cruise summaries return. The carousel then renders from a warmed cache.
    if (isWaitingForOfferDetails) return

    for (const { requests } of candidates) {
      dispatch(submitLoyaltyPointsBurnCalculationRequest(requests.standard, { viewPriceInPoints: true }))
      if (requests.member) {
        dispatch(submitLoyaltyPointsBurnCalculationRequest(requests.member, { viewPriceInPoints: true }))
      }
    }
    // customerId is included so burn calculations are re-dispatched (under the
    // correct key) if auth loads after offer details resolve.
  }, [candidates, customerId, dispatch, isWaitingForOfferDetails])

  const pointsByOfferId = useMemo(() => new Map(candidates.map(({ id, requests }) => {
    const memberPoints = requests.member
      ? burnCalculations?.[buildBurnKey(requests.member, customerId)]?.data?.priceInPoints
      : undefined
    const standardPoints = burnCalculations?.[buildBurnKey(requests.standard, customerId)]?.data?.priceInPoints
    return [id, memberPoints ?? standardPoints]
  })), [burnCalculations, candidates, customerId])

  const isWaitingForBurnPrices = candidates.length > 0
    && candidates.some(({ id, requests }) => {
      const calculation = burnCalculations?.[buildBurnKey(requests.standard, customerId)]
      return pointsByOfferId.get(id) === undefined && !calculation?.error
    })

  const isFullyCached = useMemo(() => {
    if (offerList.fetching || offerList.offerIds.length === 0) {
      return false
    }

    const allOffersResolved = processingOfferIds.every(id => (
      !!(offers[id] || offerErrors[id] || cruiseOfferErrors[id])
    ))
    if (!allOffersResolved || candidates.length === 0) {
      return false
    }

    return candidates.every(({ requests }) => {
      const calculation = burnCalculations?.[buildBurnKey(requests.standard, customerId)]
      return calculation?.data?.priceInPoints !== undefined || calculation?.error
    })
  }, [burnCalculations, candidates, customerId, cruiseOfferErrors, offerErrors, offerList.fetching, offerList.offerIds.length, processingOfferIds, offers])

  const offerIdsWithBurnPrice = useMemo(
    () => processingOfferIds.filter(id => pointsByOfferId.get(id) !== undefined),
    [pointsByOfferId, processingOfferIds],
  )

  const mustResolveAllBurnPrices = isWaitingForBurnPrices && !isFullyCached
  const shouldShowLoadingState = !isFullyCached && (
    offerList.fetching
    || isWaitingForOfferDetails
    || mustResolveAllBurnPrices
  )

  const cashPriceCap = useMemo(() => {
    if (!showOffersWithinBalance || burnPointsLimit === undefined || isWaitingForOfferDetails || isWaitingForBurnPrices) {
      return undefined
    }

    return getCashPriceCap(burnPointsLimit, candidates.flatMap(({ requests }) => {
      const points = burnCalculations?.[buildBurnKey(requests.standard, customerId)]?.data?.priceInPoints
      return points ? [{ ...requests.standard, priceInPoints: points }] : []
    }))
  }, [burnCalculations, burnPointsLimit, candidates, customerId, isWaitingForBurnPrices, isWaitingForOfferDetails, showOffersWithinBalance])

  const sortedOfferList = useMemo(() => {
    const cachedResolvedList = resolvedListsRef.current.get(resolvedListCacheKey)

    if (shouldShowLoadingState) {
      if (cachedResolvedList && !offerList.fetching) {
        return cachedResolvedList
      }

      return {
        ...offerList,
        fetching: true,
        offerIds: [],
      }
    }

    if (candidates.length === 0) {
      if (
        allowSearchOrderFallback
        && !showOffersWithinBalance
        && !isPointsSort
        && offerList.offerIds.length > 0
      ) {
        return {
          ...offerList,
          offerIds: take(offerList.offerIds, SPEND_POINTS_CAROUSEL_RENDER_LIMIT),
        }
      }

      return { ...offerList, offerIds: [] }
    }

    const offerIdsWithinBalance = showOffersWithinBalance && burnPointsLimit !== undefined
      ? offerIdsWithBurnPrice.filter(id => {
          const points = pointsByOfferId.get(id)
          return points !== undefined && points <= burnPointsLimit
        })
      : offerIdsWithBurnPrice

    if (!isPointsSort) {
      const resolvedList = {
        ...offerList,
        offerIds: take(offerIdsWithinBalance, SPEND_POINTS_CAROUSEL_RENDER_LIMIT),
      }
      if (resolvedList.offerIds.length > 0) {
        resolvedListsRef.current.set(resolvedListCacheKey, resolvedList)
      }
      return resolvedList
    }

    const direction = sortOption === 'lowest-points' ? 1 : -1
    const sortedOfferIds = [...offerIdsWithinBalance].sort((leftId, rightId) => {
      const leftPoints = pointsByOfferId.get(leftId)
      const rightPoints = pointsByOfferId.get(rightId)
      if (leftPoints === undefined) return rightPoints === undefined ? 0 : 1
      if (rightPoints === undefined) return -1
      return (leftPoints - rightPoints) * direction
    })

    const resolvedList = {
      ...offerList,
      offerIds: take(sortedOfferIds, SPEND_POINTS_CAROUSEL_RENDER_LIMIT),
    }

    if (resolvedList.offerIds.length > 0) {
      resolvedListsRef.current.set(resolvedListCacheKey, resolvedList)
    }

    return resolvedList
  }, [allowSearchOrderFallback, burnPointsLimit, candidates.length, isPointsSort, offerIdsWithBurnPrice, offerList, pointsByOfferId, resolvedListCacheKey, shouldShowLoadingState, showOffersWithinBalance, sortOption])

  return { offerList: sortedOfferList, cashPriceCap }
}

export default useLoyaltySpendPointsOfferListBurnSort
